import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Card, CardContent } from "./card";
import { cn } from "../lib/cn";

/**
 * A list with nothing in it, and the one move that would change that (US-37.8, UX-21).
 *
 * <p>About a hundred and sixty places rendered `t("….empty")` as a small grey paragraph. That is
 * consistent, and it is also the shape of a screen that is broken: nothing tells a new roastery
 * whether the list is empty because there is nothing yet or because a filter is on, and nothing
 * offers the button that would fill it. So this is for the first case with an action; a filtered
 * list keeps the quiet sentence, because there the next move is to clear the filter, not to create
 * anything.
 *
 * @param action the one move that fills the list — a `Button`, or a `Button asChild` around a
 *   `Link`. Rendered as given: a wrapper that decided for the caller would have to guess which.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  testId,
}: {
  icon?: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <Card className={cn("border-dashed bg-muted/20 shadow-none", className)} data-testid={testId}>
      <CardContent className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        {Icon ? (
          <span className="flex size-11 items-center justify-center rounded-2xl bg-secondary text-primary">
            <Icon className="size-5" aria-hidden />
          </span>
        ) : null}
        <div className="max-w-md space-y-1">
          <h3 className="font-semibold tracking-tight">{title}</h3>
          <p className="text-sm leading-6 text-muted-foreground">{description}</p>
        </div>
        {action}
      </CardContent>
    </Card>
  );
}
